import React, { useMemo, useState } from 'react';
import sortBy from 'lodash/sortBy';
import isSelf from '@medflex/utils/isSelf';
import isSupportPatient from '@medflex/utils/isSupportPatient';
import { trackEvent } from '@medflex/utils/Tracking';
import Modal from '@medflex/components/stories/Modal';
import useLocationSearchParams from '@medflex/hooks/useLocationSearchParams';
import { useTranslation } from 'react-i18next';
import { Title, Invitees } from '@medflex/components/stories/Sessions/SessionsDetailModal';
import { showAlertToast, showSuccessToast } from '@medflex/components/stories/Toast';
import PermanentLink from '@medflex/components/stories/PermanentLink';
import { useSelector } from 'react-redux';
import isTeamChatId from '@medflex/utils/isTeamChatId';
import useSessionChat from '../../hooks/useSessionChat';
import { MODALS } from '../../hooks/useIsModal';
import useModal from '../../hooks/useModal';
import useAddressBookContacts from '../../hooks/useAddressBookContacts';
import useSessionRoles from '../../hooks/useSessionRoles';

const SessionChatDetailModal = () => {
  const { t } = useTranslation('messenger');
  const { id } = useLocationSearchParams();
  const { all } = useAddressBookContacts();
  const { chat, addParticipant, update, status } = useSessionChat({ id });
  const { isModerator, isOrganizer } = useSessionRoles(chat ? chat.participants : []);
  const [titleComposing, setTitleComposing] = useState(false);
  const submitting = useSelector(state => state.loading.effects.sessions.updateAuth);
  const { close, open } = useModal();
  const { participants = [], title } = chat || {};

  const groupChatContacts = useMemo(
    () =>
      all
        .filter(
          ({ user, mainContactId, pairedAt }) =>
            (typeof pairedAt !== 'undefined' || user.organizationId) &&
            !(isSelf(user) || isSupportPatient(user) || typeof mainContactId !== 'undefined'),
        )
        .map(({ user }) => ({
          userId: user.id,
          ...user,
        })),
    [all],
  );

  const contacts = useMemo(
    () =>
      sortBy(
        groupChatContacts.filter(c => !participants.some(p => p.userId === c.userId)),
        user => user.fullNameWithTitle,
      ),
    [groupChatContacts, participants],
  );

  const handleClose = event => {
    close({ modal: MODALS.sessionChatDetail, id });
    trackEvent(event, { actionText: 'close', zone: 'SessionChatDetailModal' });
  };

  const handleParticipantsAdded = async (
    { userId, reference, referenceType, firstName, lastName },
    event,
  ) => {
    try {
      await addParticipant({
        ...(userId
          ? { userId }
          : {
              reference,
              referenceType,
              firstName,
              lastName,
            }),
      });
      trackEvent(event, { actionText: 'participant added' });
      showSuccessToast(t('SessionChatDetailModal.participantAddedSuccessMessage'), null, {
        toastId: 'sessionParticipantAddedSuccess',
      });
    } catch (e) {
      showAlertToast(t('SessionChatDetailModal.participantAddedUnexpectedError'), null, {
        toastId: 'sessionParticipantAddedFailed',
      });
    }
  };

  const handleParticipantRemoved = ({ participantId }) => {
    open({ modal: 'sessionChatRemoveParticipant', participantId });
  };

  const handleTitleChanged = async newTitle => {
    try {
      await update({ title: newTitle });
      showSuccessToast(t('SessionChatDetailModal.titleChangedSuccessMessage'), null, {
        toastId: 'sessionTitleChangeSuccess',
      });
      setTitleComposing(false);
    } catch (e) {
      showAlertToast(t('SessionChatDetailModal.titleChangedUnexpectedError'), null, {
        toastId: 'sessionTitleChangeFailed',
      });
    }
  };

  const handleCopied = event => {
    showSuccessToast(t('physician:toasts.permanent_link.copied'), null, {
      toastId: 'sessionCopySuccess',
    });
    trackEvent(event, {
      zone: 'SessionChatDetailModal',
      actionText: 'copy joinLink',
    });
  };

  const handleClickGenerateNew = () => open({ modal: 'newPermanentLink', type: 'sessionChat' });

  return (
    status.loadChat === 'succeeded' && (
      <Modal
        title={
          isModerator || isOrganizer
            ? t('SessionChatDetailModal.title_organize')
            : t('SessionChatDetailModal.title_participants')
        }
        showCloseButton
        onClickCloseButton={handleClose}
        onClickOutside={handleClose}
      >
        {(isModerator || isOrganizer) && (
          <>
            <Title
              isEditable={isModerator}
              title={title}
              placeholder={t('SessionChatDetailModal.defaultTitle')}
              onSubmit={handleTitleChanged}
              onEdit={() => setTitleComposing(true)}
              onCancel={() => setTitleComposing(false)}
              loading={submitting}
              composing={titleComposing}
            />
            <PermanentLink
              title={t('SessionChatDetailModal.permanentLink.title')}
              className="mt-4"
              uri={chat && chat.joinLink}
              onCopied={handleCopied}
              onShared={event => trackEvent(event)}
              onClickGenerateNew={handleClickGenerateNew}
              canReset
              subTitle={t('SessionChatDetailModal.permanentLink.subTitle')}
            >
              <div className="text-sm text-slate-600 -mt-4">
                {t('SessionChatDetailModal.permanentLink.info')}
              </div>
            </PermanentLink>
          </>
        )}
        <Invitees
          contacts={contacts}
          contactsOnly
          maxParticipants={100}
          minParticipants={1}
          invitees={participants}
          isEditable={(isModerator || isOrganizer) && !isTeamChatId(id)}
          onSubmit={handleParticipantsAdded}
          onDelete={handleParticipantRemoved}
        />
      </Modal>
    )
  );
};

export default SessionChatDetailModal;
